import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  Building2,
  FileText,
  ChevronDown,
  Check,
  X,
  MessageSquare,
  Lightbulb,
} from "lucide-react";
import { API_WFH_TIMESHEET_MANAGER_ACTION } from "../../config/Endpoints";
import axios from "axios";
import Cookies from "js-cookie";
import { showSuccess, showError } from "../../v2/utils/toast.ts";
import { ThreeDots } from "react-loader-spinner";
import { Button } from "../../v2/components/ui/button/button.tsx";
import { Badge, ResponsiveDataTable } from "../../v2/components/ui/index.ts";
import { Modal } from "../../v2/components/ui/modal/modal.tsx";
import { displayFormatDate } from "../../utils/helpers";

import s from "./approve-wfh-timesheet-modal.module.css";

/* ─── Formatters ─── */

function fmtDuration(minutes) {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}m`;
}

function fmtHours(minutes) {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `${min}m`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}m`;
}

function fmtTaskStatus(status) {
  if (status === "COMPLETED") return "Completed";
  if (status === "INPROGRESS") return "In-Progress";
  return status ?? "—";
}

function fmtEntryTitle(title) {
  const normalized = String(title ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ");
  if (!normalized) return "—";
  return normalized
    .split(" ")
    .map((w) =>
      w.length <= 1 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

/* ─── Grouping ─── */

function isTruthyAutoApproved(value) {
  if (value === true) return true;
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === "true";
}

function isAutoApprovedGroup(group, approval, selectedRequest) {
  return (
    isTruthyAutoApproved(approval?.isAutoApproved) ||
    isTruthyAutoApproved(approval?.isAutoapprovde) ||
    isTruthyAutoApproved(selectedRequest?.isAutoApproved) ||
    isTruthyAutoApproved(selectedRequest?.isAutoapprovde) ||
    group.entries.some(
      (entry) =>
        isTruthyAutoApproved(entry?.isAutoApproved) ||
        isTruthyAutoApproved(entry?.isAutoapprovde),
    )
  );
}

function groupTimesheetEntries(entries) {
  if (!entries?.length) return [];
  const map = new Map();

  for (const entry of entries) {
    const isMisc = entry.titleType === "OTHERS";
    const key = isMisc
      ? "others"
      : entry.projectId != null
        ? `project-${entry.projectId}`
        : `project-${String(entry.projectName || "unnamed").replace(/\s+/g, "-")}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        label: isMisc ? "Miscellaneous" : entry.projectName || "Project",
        isMisc,
        entries: [],
        approverId: entry.approverId,
        approverType: entry.approverType,
        projectId: entry.projectId ?? null,
      });
    }
    map.get(key).entries.push(entry);
  }
  return Array.from(map.values());
}

/* ─── Approval resolution ─── */

function sameProjectId(a, b) {
  const left = a ?? null;
  const right = b ?? null;
  if (left == null || right == null) return left === right;
  return Number(left) === Number(right);
}

function getApprovalsForGroup(group, approvals) {
  if (!approvals?.length) return [];

  return approvals.filter(
    (a) => sameProjectId(a.projectId, group.projectId),
  );
}

function normalizeApprovalStatus(status) {
  return String(status ?? "").trim().toUpperCase();
}

function resolveSkippedApprovalForGroup(approval, groupApprovals) {
  if (normalizeApprovalStatus(approval?.status) !== "SKIPPED") {
    return approval;
  }

  return (
    groupApprovals.find((candidate) => {
      const status = normalizeApprovalStatus(candidate?.status);
      return status === "APPROVED" || status === "REJECTED";
    }) ??
    groupApprovals.find(
      (candidate) => normalizeApprovalStatus(candidate?.status) === "PENDING",
    ) ??
    approval
  );
}

function hasApprovalForLoggedInUser(group, approvals, loginId) {
  if (loginId == null) return false;
  const forProject = getApprovalsForGroup(group, approvals);
  if (!forProject?.length) return false;

  return forProject.some(
    (a) => a?.approverId != null && Number(a.approverId) === Number(loginId),
  );
}

function isRmApproverForTimesheet(approvals, loginId) {
  if (loginId == null || !approvals?.length) return false;
  return approvals.some(
    (a) =>
      a?.approverId != null &&
      Number(a.approverId) === Number(loginId) &&
      String(a.approverType ?? "").toUpperCase() === "RM",
  );
}

function getGroupApproval(group, approvals, loginId, canViewAllGroups = false) {
  const forProject = getApprovalsForGroup(group, approvals);
  if (!forProject.length) return null;

  const loggedInApproval = forProject.find(
    (a) => a?.approverId != null && Number(a.approverId) === Number(loginId),
  );
  if (loggedInApproval && !canViewAllGroups) {
    return resolveSkippedApprovalForGroup(loggedInApproval, forProject);
  }

  if (!group.isMisc) {
    const dlApproval = forProject.find(
      (a) => String(a?.approverType ?? "").toUpperCase() === "DL",
    );
    if (dlApproval) return resolveSkippedApprovalForGroup(dlApproval, forProject);
  }

  if (group.isMisc) {
    const rmApproval = forProject.find(
      (a) => String(a?.approverType ?? "").toUpperCase() === "RM",
    );
    if (rmApproval) return resolveSkippedApprovalForGroup(rmApproval, forProject);
  }

  const decided = forProject.filter(
    (a) => a.status === "APPROVED" || a.status === "REJECTED",
  );
  if (decided.length) {
    return decided.reduce((a, b) =>
      (b.actedAt || "") > (a.actedAt || "") ? b : a,
    );
  }

  if (loggedInApproval && String(loggedInApproval.approverType ?? "").toUpperCase() === "RM") {
    return resolveSkippedApprovalForGroup(loggedInApproval, forProject);
  }

  const fallbackApproval =
    forProject.find((a) => a.approverType === group.approverType) ??
    forProject[0];

  return resolveSkippedApprovalForGroup(fallbackApproval, forProject);
}

function isSuperAdminUser(employee) {
  const roles = employee?.roles;
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => String(r).trim().toUpperCase() === "SUPERADMIN");
}

function superAdminHasDlOrRmApprovalForGroup(group, loginId, approvals) {
  if (loginId == null || !approvals?.length) return false;
  return approvals.some((a) => {
    const sameProject =
      (a.projectId ?? null) === (group.projectId ?? null) ||
      (a.projectId != null &&
        group.projectId != null &&
        Number(a.projectId) === Number(group.projectId));
    if (!sameProject) return false;
    if (Number(a.approverId) !== Number(loginId)) return false;
    const t = a.approverType;
    return t === "DL" || t === "RM";
  });
}

function isFinalizedApprovalStatus(status) {
  const st = String(status ?? "PENDING").toUpperCase();
  return st === "APPROVED" || st === "REJECTED" || st === "SKIPPED";
}

function badgeVariant(status) {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return "success";
  if (st === "REJECTED") return "danger";
  if (st === "SKIPPED") return "neutral";
  return "warning";
}

function badgeLabel(status) {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return "Approved";
  if (st === "REJECTED") return "Rejected";
  if (st === "SKIPPED") return "Skipped";
  return "Pending";
}

function statusAccentClass(status) {
  const st = String(status).toUpperCase();
  if (st === "APPROVED") return s.groupCardApproved;
  if (st === "REJECTED") return s.groupCardRejected;
  if (st === "SKIPPED") return s.groupCardSkipped;
  return s.groupCardPending;
}

/* ─── Table columns ─── */

const TABLE_COLUMNS = [
  {
    id: "category",
    header: "Category",
    accessor: "category",
    sortable: true,
    getTitleText: (row) => String(row.category ?? ""),
    render: (row) => (
      <span className={s.categoryBadge}>{row.category}</span>
    ),
  },
  {
    id: "description",
    header: "Task",
    accessor: "description",
    sortable: true,
    getTitleText: (row) => String(row.description ?? ""),
    render: (row) => <span className={s.taskDescription}>{row.description}</span>,
  },
  {
    id: "taskStatus",
    header: "Task Status",
    accessor: "taskStatus",
    sortable: true,
  },
  {
    id: "timeLabel",
    header: "Time",
    accessor: "timeLabel",
    sortable: true,
    nowrap: true,
    render: (row) => (
      <span className={s.timeCell}>{row.timeLabel}</span>
    ),
  },
];

/* ═══════════════════════════════════════ Component ═══════════════════════════════════════ */

const ApproveWFHTimesheetModal = ({
  open,
  onClose,
  timesheet,
  selectedRequest,
  onActionComplete,
}) => {
  const [groupActions, setGroupActions] = useState({});
  const [expandedEntries, setExpandedEntries] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState(null);
  const [submittingChoice, setSubmittingChoice] = useState(null);

  const accessToken = Cookies.get("accessToken");
  const userData = localStorage.getItem("userData");
  const employee = userData ? JSON.parse(userData) : null;
  const employeeId = employee?.id;

  const entryList = timesheet?.length ? timesheet : selectedRequest?.entries ?? [];
  const projectGroups = useMemo(() => groupTimesheetEntries(entryList), [entryList]);
  const loginId = employeeId == null || Number.isNaN(Number(employeeId))
    ? null
    : Number(employeeId);
  const canViewAllGroups = useMemo(
    () =>
      isSuperAdminUser(employee) ||
      isRmApproverForTimesheet(selectedRequest?.approvals, loginId),
    [employee, loginId, selectedRequest?.approvals],
  );
  const visibleProjectGroups = useMemo(() => {
    if (canViewAllGroups) return projectGroups;

    return projectGroups.filter((group) =>
      hasApprovalForLoggedInUser(group, selectedRequest?.approvals, loginId),
    );
  }, [canViewAllGroups, loginId, projectGroups, selectedRequest?.approvals]);

  const totalDurationMinutes =
    canViewAllGroups && selectedRequest?.totalDurationMinutes != null
      ? selectedRequest.totalDurationMinutes
      : visibleProjectGroups.reduce(
      (total, group) =>
        total +
        group.entries.reduce((sum, entry) => sum + (Number(entry.duration) || 0), 0),
      0,
    );

  const actionableGroupKeys = useMemo(() => {
    const baseKeys = visibleProjectGroups
      .filter((g) => {
        const approval = getGroupApproval(
          g,
          selectedRequest?.approvals,
          loginId,
          canViewAllGroups,
        );
        return !isFinalizedApprovalStatus(approval?.status);
      })
      .map((g) => g.key);

    if (!isSuperAdminUser(employee)) return baseKeys;

    return baseKeys.filter((key) => {
      const g = visibleProjectGroups.find((x) => x.key === key);
      return (
        g &&
        superAdminHasDlOrRmApprovalForGroup(
          g,
          loginId,
          selectedRequest?.approvals,
        )
      );
    });
  }, [
    canViewAllGroups,
    employee,
    loginId,
    selectedRequest,
    visibleProjectGroups,
  ]);

  const hasStagedGroups = useMemo(
    () => actionableGroupKeys.some((k) => groupActions[k]?.staged),
    [actionableGroupKeys, groupActions],
  );

  /* ─── Helpers ─── */

  const updateGroup = useCallback((key, patch) => {
    setGroupActions((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { staged: "", comment: "", error: "" }), ...patch },
    }));
  }, []);

  const toggleEntries = useCallback((key) => {
    setExpandedEntries((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /* ─── Reset ─── */

  useEffect(() => {
    if (!open) return;
    setGroupActions({});
    setExpandedEntries({});
    setSubmitting(false);
    setConfirmOpen(false);
    setPendingItems(null);
    setSubmittingChoice(null);
  }, [open, selectedRequest?.id]);

  /* ─── Stage actions ─── */

  const handleApprove = (groupKey) => {
    updateGroup(groupKey, { staged: "APPROVED", error: "" });
  };

  const handleReject = (groupKey) => {
    const ga = groupActions[groupKey];
    if (!ga?.comment?.trim()) {
      updateGroup(groupKey, { error: "Comment is required for reject" });
      return;
    }
    updateGroup(groupKey, { staged: "REJECTED", error: "" });
  };

  const handleUnstage = (groupKey) => {
    updateGroup(groupKey, { staged: "" });
  };

  /* ─── Global submit ─── */

  const finalizeSubmit = async (items) => {
    setSubmitting(true);
    try {
      const payload = {
        timesheetId: selectedRequest?.id,
        managerId: employeeId,
        items,
      };
      const res = await axios.post(API_WFH_TIMESHEET_MANAGER_ACTION, payload, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      let msg = res.data?.message || "Action submitted successfully";
      if (selectedRequest?.date) {
        const normalized = String(msg).trim();
        const withPeriod = normalized.endsWith(".") ? normalized : `${normalized}.`;
        msg = `${withPeriod} Timesheet for ${displayFormatDate(selectedRequest.date)}.`;
      }
      showSuccess(msg);
      setGroupActions({});
      onActionComplete();
      onClose();
    } catch (error) {
      showError(error?.response?.data?.message || "Something went wrong");
    }
    setSubmitting(false);
    setConfirmOpen(false);
    setPendingItems(null);
    setSubmittingChoice(null);
  };

  const handleSubmit = () => {
    const items = [];
    for (const key of actionableGroupKeys) {
      const ga = groupActions[key];
      if (!ga?.staged) continue;

      if (ga.staged === "REJECTED" && !ga.comment?.trim()) {
        updateGroup(key, { error: "Comment is required for reject", staged: "" });
        return;
      }

      const group = projectGroups.find((g) => g.key === key);
      if (!group) continue;

      const item = {
        action: ga.staged,
        comment: ga.comment || "",
        isEditable: false,
      };
      if (group.projectId != null) item.projectId = group.projectId;
      items.push(item);
    }

    if (!items.length) {
      showError("Please approve or reject at least one group");
      return;
    }

    const hasReject = items.some((i) => i.action === "REJECTED");
    if (hasReject) {
      setPendingItems(items);
      setConfirmOpen(true);
      return;
    }

    finalizeSubmit(items);
  };

  const handleConfirmResubmission = (allow) => {
    setSubmittingChoice(allow ? "yes" : "no");
    const stamped = (pendingItems || []).map((i) =>
      i.action === "REJECTED" ? { ...i, isEditable: Boolean(allow) } : i,
    );
    finalizeSubmit(stamped);
  };

  const handleClose = () => {
    setGroupActions({});
    setConfirmOpen(false);
    setPendingItems(null);
    setSubmittingChoice(null);
    onClose();
  };

  /* ─── Render ─── */

  const modalTitle = selectedRequest?.employeeName
    ? `${selectedRequest.employeeName}'s Timesheet`
    : "Approve Timesheet";

  const resubmitConfirmSuffix = selectedRequest?.date
    ? ` Timesheet for ${displayFormatDate(selectedRequest.date)}.`
    : "";

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={modalTitle}
        size="xl"
        footer={
          actionableGroupKeys.length > 0 && (
            <div className={s.footerActions}>
              <Button
                variant="accent"
                onClick={handleSubmit}
                disabled={submitting || !hasStagedGroups}
              >
                {submitting ? (
                  <ThreeDots height="20" width="40" radius="3" color="white" />
                ) : (
                  "Submit"
                )}
              </Button>
              <Button variant="light-grey" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          )
        }
      >
        <div className={s.wrap}>
          <p className={s.subtitle}>
            {selectedRequest?.date
              ? `Date: ${displayFormatDate(selectedRequest.date)}`
              : ""}
            {selectedRequest?.date ? " · " : ""}
            {fmtHours(totalDurationMinutes)} total
          </p>

          {visibleProjectGroups.length === 0 ? (
            <p className={s.emptyState}>No timesheet entries</p>
          ) : (
            visibleProjectGroups.map((group) => {
              const approval = getGroupApproval(
                group,
                selectedRequest?.approvals,
                loginId,
                canViewAllGroups,
              );
              const approvalStatus = approval?.status || "PENDING";
              const isActionable = actionableGroupKeys.includes(group.key);
              const ga = groupActions[group.key] ?? { staged: "", comment: "", error: "" };
              const isExpanded = !!expandedEntries[group.key];
              const groupMinutes = group.entries.reduce(
                (t, e) => t + (Number(e.duration) || 0),
                0,
              );

              const rows = group.entries.map((entry, idx) => ({
                id: `${group.key}-${idx}`,
                category: fmtEntryTitle(entry.titleName),
                description: entry.description || "—",
                taskStatus: fmtTaskStatus(entry.taskStatus),
                timeLabel: fmtDuration(entry.duration),
              }));

              const isDecided =
                approvalStatus === "APPROVED" || approvalStatus === "REJECTED";
              const isAutoApproved =
                approvalStatus === "APPROVED" &&
                isAutoApprovedGroup(group, approval, selectedRequest);

              return (
                <div
                  key={group.key}
                  className={clsx(
                    s.groupCard,
                    statusAccentClass(ga.staged || approvalStatus),
                    ga.staged && s.groupCardStaged,
                  )}
                >
                  {/* ── Header ── */}
                  <div className={s.groupHeader}>
                    <div className={s.groupIcon}>
                      {group.isMisc ? (
                        <FileText size={20} />
                      ) : (
                        <Building2 size={20} />
                      )}
                    </div>
                    <div className={s.groupTitleBlock}>
                      <h4 className={s.groupName}>{group.label}</h4>
                      <p className={s.groupSubtitle}>
                        {group.isMisc
                          ? `${approval?.approverType || "RM"}: ${approval?.approverName || "—"}`
                          : `${approval?.approverType || group.approverType}: ${approval?.approverName || "—"}`}
                      </p>
                    </div>
                    <div className={s.groupStats}>
                      <Badge variant={badgeVariant(ga.staged || approvalStatus)}>
                        {ga.staged
                          ? `Staged: ${badgeLabel(ga.staged)}`
                          : badgeLabel(approvalStatus)}
                      </Badge>
                      <span className={s.groupHours}>{fmtHours(groupMinutes)}</span>
                    </div>
                  </div>

                  {/* ── Action history (decided groups) ── */}
                  {isDecided && approval && (
                    <div className={s.actionHistory}>
                      <div className={s.historyLine}>
                        <span
                          className={clsx(
                            s.historyIcon,
                            approvalStatus === "APPROVED"
                              ? s.historyIconApproved
                              : s.historyIconRejected,
                          )}
                        >
                          {approvalStatus === "APPROVED" ? (
                            <Check size={14} />
                          ) : (
                            <X size={14} />
                          )}
                        </span>
                        <span>
                          {isAutoApproved ? (
                            <>
                              Auto Approved
                              {approval.actedAt
                                ? ` on ${fmtDateTime(approval.actedAt)}`
                                : ""}
                            </>
                          ) : (
                            <>
                              {badgeLabel(approvalStatus)} by{" "}
                              {approval.approverName || "—"} (
                              {approval.approverType || "—"})
                              {approval.actedAt
                                ? ` on ${fmtDateTime(approval.actedAt)}`
                                : ""}
                            </>
                          )}
                        </span>
                      </div>
                      {approval.comment && (
                        <div className={s.commentLine}>
                          <MessageSquare size={14} className={s.commentIcon} />
                          <span>{approval.comment}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Entries toggle ── */}
                  <button
                    type="button"
                    className={s.entriesToggle}
                    onClick={() => toggleEntries(group.key)}
                  >
                    <ChevronDown
                      size={16}
                      className={clsx(
                        s.toggleChevron,
                        isExpanded && s.toggleChevronOpen,
                      )}
                    />
                    {isExpanded ? "Hide" : "Show"} Time Entries (
                    {group.entries.length})
                  </button>

                  {/* ── Entries table ── */}
                  {isExpanded && (
                    <div className={s.entriesTableWrap}>
                      <ResponsiveDataTable
                        pagination={false}
                        columns={TABLE_COLUMNS}
                        data={rows}
                        aria-label={`Tasks for ${group.label}`}
                        emptyState={
                          <span className={s.emptyState}>No entries</span>
                        }
                      />
                    </div>
                  )}

                  {/* ── Action bar (PENDING only) ── */}
                  {isActionable && !ga.staged && (
                    <div className={s.actionBar}>
                      <div className={s.actionInputRow}>
                        <div
                          className={clsx(
                            s.commentInput,
                            ga.error && s.commentInputError,
                          )}
                        >
                          <input
                            type="text"
                            placeholder="Add comment or reason (required for reject)..."
                            value={ga.comment}
                            onChange={(e) =>
                              updateGroup(group.key, {
                                comment: e.target.value,
                                error: "",
                              })
                            }
                          />
                        </div>
                        <div className={s.actionButtons}>
                          <button
                            type="button"
                            className={s.approveBtn}
                            onClick={() => handleApprove(group.key)}
                          >
                            <Check size={14} /> Approve
                          </button>
                          <button
                            type="button"
                            className={s.rejectBtn}
                            onClick={() => handleReject(group.key)}
                          >
                            <X size={14} /> Reject
                          </button>
                        </div>
                      </div>
                      {ga.error ? (
                        <p className={s.errorText}>{ga.error}</p>
                      ) : (
                        <p className={s.helperText}>
                          <Lightbulb
                            size={12}
                            style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }}
                          />
                          Comment is optional for approve, but{" "}
                          <strong>required for reject</strong>
                        </p>
                      )}
                    </div>
                  )}

                  {/* ── Staged indicator ── */}
                  {isActionable && ga.staged && (
                    <div className={s.actionBar}>
                      <div
                        className={clsx(
                          s.stagedIndicator,
                          ga.staged === "APPROVED"
                            ? s.stagedApproved
                            : s.stagedRejected,
                        )}
                      >
                        {ga.staged === "APPROVED" ? (
                          <Check size={14} />
                        ) : (
                          <X size={14} />
                        )}
                        Staged: {badgeLabel(ga.staged)}
                        {ga.comment && ` — "${ga.comment}"`}
                        <button
                          type="button"
                          className={s.unstageBtn}
                          onClick={() => handleUnstage(group.key)}
                        >
                          Undo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Modal>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!submitting) {
            setConfirmOpen(false);
            setPendingItems(null);
          }
        }}
        title="Allow Employee to Resubmit?"
        size="sm"
        footer={
          <div className={s.footerActions}>
            <Button
              variant="light-grey"
              onClick={() => handleConfirmResubmission(false)}
              disabled={submitting}
            >
              {submittingChoice === "no" ? (
                <ThreeDots height="20" width="40" radius="3" color="#666" />
              ) : (
                "No, don't allow"
              )}
            </Button>
            <Button
              variant="accent"
              onClick={() => handleConfirmResubmission(true)}
              disabled={submitting}
            >
              {submittingChoice === "yes" ? (
                <ThreeDots height="20" width="40" radius="3" color="white" />
              ) : (
                "Yes, allow"
              )}
            </Button>
          </div>
        }
      >
        <p className={s.confirmCopy}>
          You have rejected at least one group. Do you want employee
          to edit and resubmit the timesheet?
          {resubmitConfirmSuffix}
        </p>
      </Modal>
    </>
  );
};

export default ApproveWFHTimesheetModal;
